import { createContext, type ReactNode, useContext, useMemo } from "react";
import type { InteractionCitations } from "./derive-session-context";
import type { SessionContextView } from "./session-context-drawer";
import type { ChatCitations } from "./use-chat-citations";

// Markdown components get only a tag's attributes, so LLMMessages provides the rest for each message.
// Holds only this message's interaction, so a pill re-renders only when that interaction's citations change.
type InlineCitationsContextValue = {
  interactionId: string;
  interactionCitations: InteractionCitations | undefined;
  resolveLookedUpCitations: ChatCitations["resolveLookedUpCitations"];
  setView: (view: SessionContextView) => void;
};

const InlineCitationsContext =
  createContext<InlineCitationsContextValue | null>(null);

export const useInlineCitationsContext = () =>
  useContext(InlineCitationsContext);

// Memoized because a fresh value re-renders every inline pill under it.
export const InlineCitationsProvider = ({
  interactionId,
  interactionCitations,
  resolveLookedUpCitations,
  setView,
  children,
}: Omit<InlineCitationsContextValue, "interactionId"> & {
  interactionId: string | undefined;
  children: ReactNode;
}) => {
  const value = useMemo(
    () =>
      interactionId
        ? {
            interactionId,
            interactionCitations,
            resolveLookedUpCitations,
            setView,
          }
        : null,
    [interactionId, interactionCitations, resolveLookedUpCitations, setView],
  );
  return (
    <InlineCitationsContext.Provider value={value}>
      {children}
    </InlineCitationsContext.Provider>
  );
};
